import Immutable from 'immutable';
import { createStructuredSelector } from 'reselect';

import logger from 'logger';
import { PAYPAL_STATUS } from 'enum/helpers';
import { ENUM_IDENTITY_STATUS } from 'enum/profile';
import ApiService from 'services/ApiService/ApiService';
import { isValidResponse } from 'utils/helpers';
import makeAction from 'utils/makeAction';
import parseCatch from 'utils/parseCatch';

import { ROSTER_URL } from './Api';


const FETCH_ROSTER = 'cam4/studio/FETCH_ROSTER';
const SET_ACCOUNT_STATUS_BY_USERNAME = 'cam4/studio/SET_ACCOUNT_STATUS_BY_USERNAME';
const UPDATE_ROSTER = 'cam4/studio/UPDATE_ROSTER';

export const NAME = 'roster';

const initialState = Immutable.fromJS({
  accountStatusByUsername: {},
  activePerformers: [],
  inactivePerformers: [],
  bannedPerformers: [],
  isFetchingRosters: false,
});

export default function reducer(state = initialState, action) {
  switch (action.type) {
    case UPDATE_ROSTER: {
      const { APPROVED, PENDING } = ENUM_IDENTITY_STATUS;
      const usernameToLowercase = (username) => state.get('accountStatusByUsername')[username.toLowerCase()];
      const usernameWithStatus = (username, status) => usernameToLowercase(username) === status;
      const activePerformers = action.roster
        .filter(
          ({ username, lockAccount }) =>
            usernameToLowercase(username) && usernameWithStatus(username, APPROVED) && lockAccount === 0
        )
        .map((user) => Object.assign({ accountStatusByUsername: usernameToLowercase(user.username) }, user));

      const inactivePerformers = action.roster
        .filter(
          ({ username, lockAccount }) =>
            (!usernameToLowercase(username) || usernameWithStatus(username, PENDING)) && lockAccount === 0
        )
        .map((user) =>
          Object.assign(
            { accountStatusByUsername: usernameToLowercase(user.username) },
            user
          )
        );
      const bannedPerformers = action.roster
        .filter(({ lockAccount }) => lockAccount > 0)
        .map((user) =>
          Object.assign(
            { accountStatusByUsername: usernameToLowercase(user.username) },
            user
          )
        );

      return state
        .set('activePerformers', Immutable.fromJS(activePerformers))
        .set('inactivePerformers', Immutable.fromJS(inactivePerformers))
        .set('bannedPerformers', Immutable.fromJS(bannedPerformers));
    }

    case SET_ACCOUNT_STATUS_BY_USERNAME: {
      return state.set('accountStatusByUsername', action.roster);
    }

    case FETCH_ROSTER: {
      return state.set('isFetchingRosters', action.isFetchingRosters);
    }

    default: {
      return state;
    }
  }
}

const performers = (state) =>
  state[NAME].get('activePerformers').concat(
    state[NAME].get('inactivePerformers').concat(state[NAME].get('bannedPerformers'))
  );
const inactivePerformers = (state) => state[NAME].get('inactivePerformers');
const activePerformers = (state) => state[NAME].get('activePerformers');
const bannedPerformers = (state) => state[NAME].get('bannedPerformers');
const isFetchingRosters = (state) => state[NAME].get('isFetchingRosters');

const updateRoster = makeAction(UPDATE_ROSTER, 'roster');
const setAccountStatusByUsername = makeAction(SET_ACCOUNT_STATUS_BY_USERNAME, 'roster');
const setIsFetching = makeAction(FETCH_ROSTER, 'isFetchingRosters');

function checkPaypalStatus(paypalStatus) {
  if (paypalStatus !== PAYPAL_STATUS.NONE) {
    return ENUM_IDENTITY_STATUS.APPROVED;
  }

  return ENUM_IDENTITY_STATUS.PENDING;

}

function getRosterStatus(studioOwnerName) {
  const url = ApiService.substitute(ROSTER_URL, { studioOwnerName: encodeURI(studioOwnerName) });

  return ApiService.get(url).then((res) => {
    const object = {};
    res.data.forEach((key) => {
      object[key.username.toLowerCase()] = checkPaypalStatus(key.paypalStatus.paypalStatus);
    });

    return object;
  });
}

function getRoster(studioOwnerName) {
  const url = ApiService.substitute(ROSTER_URL, { studioOwnerName: encodeURI(studioOwnerName) });

  return (dispatch) => {
    dispatch(setIsFetching(true));
    ApiService.get(url).then(
      (res) => {
        if (isValidResponse(res)) {
          getRosterStatus(studioOwnerName)
            .then((data) => dispatch(setAccountStatusByUsername(data)))
            .then(() => {
              dispatch(setIsFetching(false));
              dispatch(updateRoster(res.data));
            });
        } else {
          logger.warn({ msg: 'Fetch roster', error: res.statusText });
          dispatch(setIsFetching(false));
        }
      },
      (err) => {
        parseCatch(err, true);
        dispatch(setIsFetching(false));
      }
    );
  };
}

export const selector = createStructuredSelector({
  inactivePerformers,
  activePerformers,
  bannedPerformers,
  performers,
  isFetchingRosters,
});

export const actionCreators = { getRoster };
